import * as React from 'react';
import { cn } from '../../lib/cn';
import {
  sanitizeNumericText,
  sanitizeDiscountText,
} from '../../lib/numeric_text';
import { useFormFieldContext } from '../form_field/form_field';

export interface AutoGrowTextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'rows'
> {
  error?: boolean;
  /**
   * Collapsed (single-row) height, to line up with the plain `<Input>`s beside
   * it: `'sm'` = 2rem (`h-8`), `'md'` = 2.25rem (`h-9`), `'lg'` = 2.5rem
   * (`h-10`).
   */
  size?: 'sm' | 'md' | 'lg';
  /** Rows the field occupies when empty. Defaults to 1 — an `<Input>` look-alike. */
  minRows?: number;
  /** Rows after which the field stops growing and scrolls instead. Unbounded by default. */
  maxRows?: number;
  /**
   * Allow literal newlines. Off by default: this component exists to WRAP long
   * text, not to change what a single-line field stores, so Enter keeps its
   * `<input>` meaning (submit the surrounding form) and pasted newlines
   * collapse to spaces.
   */
  allowNewlines?: boolean;
  /**
   * Numeric cell. Keeps the field number-only as it is typed (digits, one
   * decimal point, one leading minus), asks mobile for the decimal keypad, and
   * defaults to end-aligned tabular figures so decimals line up down the
   * column — the parts of `<input type="number">` that matter in an accounting
   * grid, on a field that now has to wrap.
   *
   * What it deliberately does NOT reproduce is the spinner and its arrow-key
   * stepping: a `<textarea>` has no native equivalent, and Xero's line-item
   * grid has no steppers either. `min` / `max` are likewise not forwarded —
   * they were never enforced while typing in the fields this replaced (only on
   * native form validation, which these grids don't use — they validate in
   * app code), so bounds belong in the consumer's `onChange` / validation.
   */
  numeric?: boolean;
  /**
   * Dual-mode numeric/percent cell (Xero-style line-item discount): keeps the
   * field number-only PLUS a single trailing `%` as it is typed
   * ({@link sanitizeDiscountText}), instead of `numeric`'s plain-number-only
   * filtering ({@link sanitizeNumericText}). Never combine with `numeric` —
   * pick one. Shares the same uncommitted-draft handling as `numeric` so
   * typing "4" → "45" → "45%" is never snapped or reformatted mid-keystroke.
   */
  numericPercent?: boolean;
}

/** `parseFloat` over a computed-style length, with a 0 fallback for '' / 'normal'. */
function px(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * A textarea that looks like `<Input>` (same `nv-input` surface, single row by
 * default) but wraps long content onto more lines and grows to fit it, so no
 * entered text is ever clipped or hidden behind a horizontal scroll.
 *
 * Use it instead of `<Input>` for cells inside a table row — line item
 * descriptions, units, custom columns — where the row height should follow the
 * content. Numeric cells (quantity, a rate override) pass `numeric`, which
 * restores number-only typing and the decimal keypad on top of the wrapping;
 * money cells stay on `<MoneyInput autoGrow>`, which keeps its minor-unit
 * conversion and renders through this component.
 */
export const AutoGrowTextarea = React.forwardRef<
  HTMLTextAreaElement,
  AutoGrowTextareaProps
>(
  (
    {
      className,
      error = false,
      size = 'sm',
      minRows = 1,
      maxRows,
      allowNewlines = false,
      numeric = false,
      numericPercent = false,
      onChange,
      onKeyDown,
      onBlur,
      value,
      ...htmlProps
    },
    ref,
  ) => {
    const element_ref = React.useRef<HTMLTextAreaElement | null>(null);
    const formFieldContext = useFormFieldContext();

    // ── Uncommitted numeric text ────────────────────────────────────────────
    // A controlled numeric cell renders `String(state)`, so every keystroke is
    // a parse-and-reformat round trip that DELETES in-progress text: type
    // "12." and `Number('12.')` is 12, the parent re-renders "12", and the
    // decimal point vanishes — 12.5 becomes impossible to enter and the line
    // silently books 125. `<input type="number">` had no such failure mode:
    // the browser reports a half-typed "12." as `''` (bad input), which matched
    // the `''` value prop, so React never overwrote the raw text. A <textarea>
    // has no bad-input concept, so the field must own the uncommitted text
    // itself — exactly what `<MoneyInput>`'s `display` state does, which is why
    // money cells never had this bug.
    const [draft, set_draft] = React.useState<string | null>(null);
    const is_controlled = value !== undefined;
    const is_numeric_like = numeric || numericPercent;
    const use_draft = is_numeric_like && is_controlled && draft !== null;

    React.useEffect(() => {
      if (draft === null) return;
      // Keep the draft while it still PARSES to the value the consumer holds
      // ("12." and 12 agree). A draft that parses to something else means the
      // value changed underneath us — an item autofill, a row reset, a
      // programmatic setValue — so the incoming value wins. `NaN` is text still
      // on its way to being a number ('-', '.', '-.'): keep it. For
      // `numericPercent`, a trailing "%" also parses as NaN via `Number()` —
      // the same "still typing" branch, so a draft like "45%" is kept exactly
      // the same way as an in-progress "12." would be.
      const draft_number = Number(draft);
      if (Number.isNaN(draft_number)) return;
      if (draft_number !== Number(value)) set_draft(null);
    }, [draft, value]);

    const attach_ref = React.useCallback(
      (el: HTMLTextAreaElement | null) => {
        element_ref.current = el;
        if (typeof ref === 'function') {
          ref(el);
        } else if (ref !== null) {
          ref.current = el;
        }
      },
      [ref],
    );

    const resize = React.useCallback(() => {
      const el = element_ref.current;
      if (el === null) return;

      // Collapse before measuring: `scrollHeight` can never report less than
      // the element's current height, so without this the field only ever
      // grows and would stay tall after the text is deleted.
      el.style.height = 'auto';
      // ...and neutralise any CSS `min-height` for the measurement itself. A
      // stylesheet can legitimately set a floor (the line-item grid stretches
      // every field to the row height with `min-height: 100%`), and that floor
      // inflates `scrollHeight` — which we would then write back as an explicit
      // height, making the floor the new content height. That ratchets up on
      // every keystroke and never shrinks back.
      const previous_min_height = el.style.minHeight;
      el.style.minHeight = '0';
      const content_height = el.scrollHeight;
      el.style.minHeight = previous_min_height;
      // 0 means the node is not being laid out (jsdom, `display: none`, not yet
      // in the document). Leave the CSS height in charge rather than pinning
      // the field to 0px.
      if (content_height <= 0) {
        el.style.removeProperty('height');
        return;
      }

      const styles = window.getComputedStyle(el);
      const line_height = px(styles.lineHeight) || px(styles.fontSize) * 1.5;
      const frame =
        px(styles.paddingTop) +
        px(styles.paddingBottom) +
        (styles.boxSizing === 'border-box'
          ? px(styles.borderTopWidth) + px(styles.borderBottomWidth)
          : 0);
      // `scrollHeight` covers content + padding but not borders.
      const wanted =
        content_height +
        (styles.boxSizing === 'border-box'
          ? px(styles.borderTopWidth) + px(styles.borderBottomWidth)
          : 0);
      const min_height = line_height * minRows + frame;
      const max_height =
        maxRows === undefined
          ? Number.POSITIVE_INFINITY
          : line_height * maxRows + frame;

      el.style.height = `${String(Math.min(Math.max(wanted, min_height), max_height))}px`;
      el.style.overflowY = wanted > max_height ? 'auto' : 'hidden';
    }, [minRows, maxRows]);

    // Deliberately dependency-list-free: the height must track the value no
    // matter how it changed — a controlled `value` prop, a react-hook-form
    // `setValue` on a registered (uncontrolled) field, or a font/locale swap
    // that re-wraps the same string.
    React.useLayoutEffect(() => {
      resize();
    });

    // Re-wrap when the column gets narrower or wider. Height changes are
    // ignored: those are ours, and reacting to them would loop.
    React.useEffect(() => {
      const el = element_ref.current;
      if (el === null) return;
      let last_width = el.clientWidth;
      const observer = new ResizeObserver(() => {
        if (el.clientWidth === last_width) return;
        last_width = el.clientWidth;
        resize();
      });
      observer.observe(el);
      return () => {
        observer.disconnect();
      };
    }, [resize]);

    return (
      <textarea
        ref={attach_ref}
        rows={minRows}
        value={use_draft ? draft : value}
        inputMode={is_numeric_like ? 'decimal' : undefined}
        className={cn(
          'nv-input',
          'nv-input-autogrow',
          size === 'md' && 'nv-input-autogrow-md',
          size === 'lg' && 'nv-input-autogrow-lg',
          // Ahead of `className` so a call site can still opt out.
          is_numeric_like && 'text-end tabular-nums',
          error && 'nv-field-error',
          className,
        )}
        aria-describedby={formFieldContext?.describedBy}
        aria-invalid={formFieldContext?.ariaInvalid}
        onChange={(event) => {
          if (numericPercent) {
            // Number-only PLUS a single trailing "%" — see sanitizeDiscountText.
            event.target.value = sanitizeDiscountText(event.target.value);
          } else if (numeric) {
            // Number-only, same as the `<input type="number">` this replaces:
            // filter as the user types so letters and stray symbols never
            // reach the consumer's parser. Also subsumes the newline collapse
            // below — `\n` is not a numeric character.
            event.target.value = sanitizeNumericText(event.target.value);
          }
          if (!allowNewlines && /[\r\n]/.test(event.target.value)) {
            // Multi-line paste into a field whose value is single-line by
            // contract. Collapse rather than store newlines the consumer never
            // expected — same end state as pasting into `<input type="text">`.
            event.target.value = event.target.value.replace(/[\r\n]+/g, ' ');
          }
          // Remember what the user actually typed, so a value that cannot be
          // round-tripped through the consumer's parser ("12.", "-", "45%")
          // survives the re-render it triggers.
          if (is_numeric_like && is_controlled) set_draft(event.target.value);
          onChange?.(event);
          resize();
        }}
        onBlur={(event) => {
          // Editing is over: drop the uncommitted text so the field shows the
          // consumer's canonical formatting again ("12." → "12").
          set_draft(null);
          onBlur?.(event);
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (
            allowNewlines ||
            event.key !== 'Enter' ||
            event.defaultPrevented ||
            event.shiftKey ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey
          ) {
            return;
          }
          // Preserve the implicit form submission this field had as an
          // `<input>`: Enter saves the document, it does not insert a newline.
          event.preventDefault();
          event.currentTarget.form?.requestSubmit();
        }}
        {...htmlProps}
      />
    );
  },
);
AutoGrowTextarea.displayName = 'AutoGrowTextarea';
